/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Video Debug Overlay — the data model and the Web metrics provider
 * behind the on-screen playback diagnostics panel.
 *
 * An isolated module with **no inbound dependencies**: the overlay reads
 * the player, the player knows nothing about the overlay, so the panel can
 * be removed or replaced without touching playback. It depends on
 * `model/videoplayer`, `model/device` and `model/logging`, and on nothing
 * else in the app.
 *
 * Layering: the ViewModel (`viewModel/videoDebugOverlay`) owns visibility,
 * mode and the polling loop; the View renders the projected rows. Nothing
 * in `src/views/` imports this module directly.
 *
 * Four files, by what they cost a consumer rather than by category:
 *
 * | File | Contents |
 * | --- | --- |
 * | `videoDebugOverlay.types.ts` | The data model and its enums. A leaf — it imports nothing but a type. |
 * | `videoDebugOverlay.utils.ts` | The pure snapshot pipeline |
 * | `WebVideoMetricsProvider.ts` | The one class, and the only file that touches the player |
 *
 * The barrel carries the module's public contract only: the aggregate
 * snapshot, the rendered rows, the provider and the pipeline. The
 * per-group metric shapes are members of {@link TDebugMetrics} and are
 * reached through it, so they stay internal (CodingGuidelines §19.1).
 */

export {
  DebugMetricGroupId,
  DebugOverlayMode
} from "./videoDebugOverlay.types";
export type {
  IVideoMetricsProvider,
  TDebugMetricField,
  TDebugMetricGroup,
  TDebugMetrics
} from "./videoDebugOverlay.types";
export {
  NOT_AVAILABLE,
  buildDebugMetricGroups,
  copyDebugMetricsToClipboard,
  createEmptyDebugMetrics,
  formatDebugMetricsForExport
} from "./videoDebugOverlay.utils";
export {
  WebVideoMetricsProvider,
  recordLastKeyCode
} from "./WebVideoMetricsProvider";
